import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import GeneralLoading from "../../../shared-components/Loading";
import { useApiClient } from "../../../api";
import { usePagination } from "../../../hooks/usePagination";
import LocalAuthoritySearchAutocomplete from "./LocalAuthoritySearchAutocomplete";

const phaseOptionLabel = (option) => `${option.label} (${option.count})`;

const SchoolSearchAutocomplete = ({
  school,
  onChange,
  children,
  schoolGroup = null,
  showResults = false,
  subscriptionSwitch = false,
  defaultSubscribedOnly = false,
  defaultApprovalFilter = "all",
  phaseFilter = false,
  localAuthorityFilter = false,
  onFiltersChange,
  refreshKey = 0,
  sx = {},
}) => {
  const hasSingleSelect = typeof onChange === "function";
  if (!hasSingleSelect && !showResults) {
    throw new Error(
      "SchoolSearchAutocomplete requires either 'school' + 'onChange' for selection, or 'onResultsChange' for filtered results.",
    );
  }

  const [inputValue, setInputValue] = useState("");
  const [subscribedOnly, setSubscribedOnly] = useState(defaultSubscribedOnly);
  const [approvalFilter, setApprovalFilter] = useState(defaultApprovalFilter);
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [selectedLocalAuthority, setSelectedLocalAuthority] = useState(null);
  const [phaseOptions, setPhaseOptions] = useState([]);
  const [debouncedValue, setDebouncedValue] = useState(inputValue);

  // Update debouncedValue after 300ms of inactivity
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);
  const ApiClient = useApiClient();

  useEffect(() => {
    if (!phaseFilter) return;

    ApiClient.schools
      .getSchoolPhaseOptions()
      .then(setPhaseOptions)
      .catch((error) => console.error(error));
  }, [ApiClient, phaseFilter, refreshKey]);

  const filters = useMemo(
    () => ({
      schoolGroup: schoolGroup?.id,
      searchString: debouncedValue,
      baseSearch: showResults,
      subscribedOnly,
      subscriptionApprovalStatus: approvalFilter,
      phases: selectedPhases,
      localAuthority: selectedLocalAuthority?.id || null,
      refreshKey,
    }),
    [
      debouncedValue,
      showResults,
      subscribedOnly,
      schoolGroup,
      approvalFilter,
      selectedPhases,
      selectedLocalAuthority,
      refreshKey,
    ],
  );

  useEffect(() => {
    onFiltersChange?.({
      searchString: debouncedValue,
      subscribedOnly,
      subscriptionApprovalStatus: approvalFilter,
      phases: selectedPhases,
      localAuthorityId: selectedLocalAuthority?.id || null,
    });
  }, [
    approvalFilter,
    debouncedValue,
    onFiltersChange,
    selectedLocalAuthority,
    selectedPhases,
    subscribedOnly,
  ]);

  const { options, loading, page, pageInfo, handlePageChange } = usePagination(
    ({
      first,
      after,
      searchString,
      baseSearch,
      subscribedOnly,
      subscriptionApprovalStatus,
      schoolGroup,
      phases,
      localAuthority,
    }) =>
      ApiClient.schools.searchSchools({
        first,
        after,
        searchString,
        baseSearch,
        subscribedOnly,
        subscriptionApprovalStatus,
        phases,
        localAuthority,
        ...(schoolGroup ? { schoolGroup } : {}),
      }),
    20,
    filters,
  );

  useEffect(() => {
    if (!school) {
      setInputValue("");
    }
  }, [school]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          width: "100%",
          gap: 2,
          ...sx,
        }}
      >
        <Autocomplete
          sx={{ minWidth: { xs: "100%", sm: 320 }, flex: "2 1 360px" }}
          clearOnBlur={false}
          clearOnEscape={false}
          value={school}
          onChange={onChange}
          inputValue={inputValue}
          filterOptions={(x) => x} // return options as-is
          onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
          options={options}
          noOptionsText={
            inputValue.trim()
              ? "No matching schools found"
              : "Start typing a school name or postcode"
          }
          open={hasSingleSelect ? undefined : false}
          disableCloseOnSelect={!hasSingleSelect}
          getOptionLabel={(option) => option.name} // adapt if API returns differently
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                  {option.addressLine1} ({option.town})
                </Box>
              </Box>
            </li>
          )}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search for a school"
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <GeneralLoading
                        direction="row"
                        size={25}
                        message="Searching..."
                        sx={{ mr: 1 }}
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {subscriptionSwitch && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={subscribedOnly}
                  onChange={(e) => setSubscribedOnly(e.target.checked)}
                />
              }
              label="Subscribed only"
            />
            {subscribedOnly && (
              <FormControl
                size="small"
                sx={{ minWidth: 180, flex: "0 1 180px" }}
              >
                <InputLabel>Approval</InputLabel>
                <Select
                  value={approvalFilter}
                  label="Approval"
                  onChange={(e) => setApprovalFilter(e.target.value)}
                >
                  <MenuItem value="all">All schools</MenuItem>
                  <MenuItem value="approved">Approved only</MenuItem>
                  <MenuItem value="unapproved">Not approved</MenuItem>
                </Select>
              </FormControl>
            )}
          </>
        )}
        {phaseFilter && (
          <FormControl size="small" sx={{ minWidth: 220, flex: "1 1 240px" }}>
            <InputLabel id="school-phase-filter-label">
              Education phase
            </InputLabel>
            <Select
              labelId="school-phase-filter-label"
              id="school-phase-filter"
              multiple
              value={selectedPhases}
              label="Education phase"
              onChange={(event) => setSelectedPhases(event.target.value)}
              renderValue={(values) =>
                values
                  .map((value) =>
                    phaseOptions.find((option) => option.value === value),
                  )
                  .map((option, index) =>
                    option ? phaseOptionLabel(option) : values[index],
                  )
                  .join(", ")
              }
            >
              {phaseOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={selectedPhases.includes(option.value)} />
                  <ListItemText primary={phaseOptionLabel(option)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {localAuthorityFilter && (
          <LocalAuthoritySearchAutocomplete
            value={selectedLocalAuthority}
            onChange={setSelectedLocalAuthority}
          />
        )}
      </Box>

      {children?.({ options, loading, page, pageInfo, handlePageChange })}
    </>
  );
};

export default SchoolSearchAutocomplete;
